import { format } from "date-fns/format";
import { zipSync } from "fflate";

export interface RecordingFile {
  /** Filename to use inside the ZIP / for single download — YYYY-MM-DD_HH-MM-SS.mp3 */
  filename: string;
  /** Raw audio bytes */
  data: Uint8Array;
}

/**
 * Build the mandatory filename from date + time fields on the call record.
 * date: "YYYY-MM-DD", time: "HH:MM"  →  "YYYY-MM-DD_HH-MM-00_RECORDINGSID.mp3"
 */
export function buildFilename(date: string, time: string, recordingSid?: string): string {
  const formattedDate = format(new Date(date), "yyyy-MM-dd");
  const timePart = time.replace(/:/g, "-");
  const base = `${formattedDate}_${timePart}`;
  return recordingSid !== undefined ? `${base}_${recordingSid}.mp3` : `${base}.mp3`;
}

/** Trigger a browser download for a single blob */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Download a single recording directly as an .mp3 file.
 */
export function downloadSingle(recording: RecordingFile) {
  const blob = new Blob([recording.data.buffer.slice(0) as ArrayBuffer], { type: "audio/mpeg" });
  triggerDownload(blob, recording.filename);
}

/**
 * Bundle multiple recordings into a ZIP and download it.
 */
export function downloadZip(recordings: RecordingFile[], zipName = "recordings.zip") {
  const files: Record<string, Uint8Array> = {};
  // Deduplicate filenames in case two calls share the same timestamp
  const seen: Record<string, number> = {};
  for (const rec of recordings) {
    const count = seen[rec.filename] ?? 0;
    const key = count === 0 ? rec.filename : rec.filename.replace(".mp3", `_${count}.mp3`);
    seen[rec.filename] = count + 1;
    files[key] = rec.data;
  }

  const zipped = zipSync(files);
  const blob = new Blob([zipped.buffer.slice(0) as ArrayBuffer], { type: "application/zip" });
  triggerDownload(blob, zipName);
}
